import { Component } from '@theme/component';

/** Toast for wishlist add/remove and guest sign-in prompt. */
export class WishlistToast extends Component {
  requiredRefs = ['popover', 'message'];

  /** @type {number | null} */
  #hideTimeout = null;

  /** @type {HTMLElement | null} */
  #previousFocus = null;

  /** @type {number} */
  #autoHideDelay = 3000;

  /** @type {HTMLElement | null} Original parent of the popover before reparenting into a modal dialog. */
  #originalParent = null;

  connectedCallback() {
    super.connectedCallback();

    this.#autoHideDelay = parseInt(this.dataset.autoHideDelay || '5000', 10);

    this.#setupEventListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#clearHideTimeout();
    this.#removeEventListeners();
  }

  #setupEventListeners() {
    document.addEventListener('ooo-wishlist:product-added', this.#handleProductAdded);
    document.addEventListener('ooo-wishlist:product-removed', this.#handleProductRemoved);
    document.addEventListener('wk:wishlist:add:success', this.#handleProductAdded);
    document.addEventListener('wk:wishlist:remove:success', this.#handleProductRemoved);
  }

  #removeEventListeners() {
    document.removeEventListener('ooo-wishlist:product-added', this.#handleProductAdded);
    document.removeEventListener('ooo-wishlist:product-removed', this.#handleProductRemoved);
    document.removeEventListener('wk:wishlist:add:success', this.#handleProductAdded);
    document.removeEventListener('wk:wishlist:remove:success', this.#handleProductRemoved);
  }

  #handleProductAdded = () => {
    this.show('added');
  };

  #handleProductRemoved = () => {
    this.show('removed');
  };

  #setSuccessIcon() {
    if (this.refs.iconCheck) this.refs.iconCheck.hidden = false;
    if (this.refs.iconClose) this.refs.iconClose.hidden = true;
  }

  /**
   * If a modal dialog is open, move the popover inside it so the modal's
   * inertness doesn't block pointer events. Restores to original parent on hide.
   */
  #reparentForModal(popover) {
    const openDialogs = document.querySelectorAll('dialog[open]');
    const topmostDialog = openDialogs.length ? openDialogs[openDialogs.length - 1] : null;

    if (topmostDialog && !topmostDialog.contains(popover)) {
      this.#originalParent = popover.parentElement;
      topmostDialog.appendChild(popover);
    }
  }

  #restoreParent() {
    const popover = this.refs.popover;
    if (this.#originalParent && popover.parentElement !== this.#originalParent) {
      this.#originalParent.appendChild(popover);
    }
    this.#originalParent = null;
  }

  /** Focus the toast (link or popover) for accessibility. */
  #focusToast() {
    const popover = this.refs.popover;
    const focusTarget = this.refs.viewLink ?? popover;
    focusTarget.focus();
  }

  /** Show popover with current content (Liquid sets guest vs success content). */
  #showPopover() {
    this.#clearHideTimeout();
    this.#previousFocus = /** @type {HTMLElement | null} */ (document.activeElement);

    const popover = this.refs.popover;
    popover.classList.remove('wishlist-toast--hiding');
    this.#reparentForModal(popover);

    if (typeof popover.showPopover === 'function') {
      const onToggle = (/** @type {ToggleEvent} */ e) => {
        if (e.newState === 'open') {
          popover.removeEventListener('toggle', onToggle);
          this.#focusToast();
        }
      };
      popover.addEventListener('toggle', onToggle);
      popover.showPopover();
    } else {
      popover.style.display = 'block';
      setTimeout(() => this.#focusToast(), 0);
    }

    this.#hideTimeout = window.setTimeout(() => this.hide(), this.#autoHideDelay);
  }

  show(action) {
    this.#clearHideTimeout();
    this.#previousFocus = /** @type {HTMLElement | null} */ (document.activeElement);

    const message = action === 'added' ? this.dataset.addedMessage : this.dataset.removedMessage;
    this.refs.message.textContent = message || '';

    if (this.refs.viewLink) {
      this.refs.viewLink.href = this.dataset.wishlistUrl || '#';
      this.refs.viewLink.textContent = this.dataset.viewLabel || '';
    }
    this.#setSuccessIcon();

    const popover = this.refs.popover;
    const viewLink = this.refs.viewLink;
    popover.classList.remove('wishlist-toast--hiding');
    this.#reparentForModal(popover);

    if (typeof popover.showPopover === 'function') {
      const onToggle = (/** @type {ToggleEvent} */ e) => {
        if (e.newState === 'open') {
          popover.removeEventListener('toggle', onToggle);
          this.#focusToast();
        }
      };
      popover.addEventListener('toggle', onToggle);
      popover.showPopover();
    } else {
      popover.style.display = 'block';
      setTimeout(() => this.#focusToast(), 0);
    }

    this.#hideTimeout = window.setTimeout(() => {
      this.hide();
    }, this.#autoHideDelay);
  }

  hide() {
    this.#clearHideTimeout();

    const popover = this.refs.popover;
    popover.classList.add('wishlist-toast--hiding');

    const onAnimationEnd = () => {
      popover.classList.remove('wishlist-toast--hiding');
      if (typeof popover.hidePopover === 'function') {
        popover.hidePopover();
      } else {
        popover.style.display = 'none';
      }
      popover.removeEventListener('animationend', onAnimationEnd);
      this.#restoreParent();
      this.#previousFocus?.focus();
      this.#previousFocus = null;
    };

    popover.addEventListener('animationend', onAnimationEnd);
  }

  #clearHideTimeout() {
    if (this.#hideTimeout !== null) {
      clearTimeout(this.#hideTimeout);
      this.#hideTimeout = null;
    }
  }
}

if (!customElements.get('wishlist-toast')) {
  customElements.define('wishlist-toast', WishlistToast);
}
